import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Button } from '../../../components/ui/button';
import { UserPlus, X } from 'lucide-react';
import { FormateurOptions } from './SelectOptions';

export const creneauLabel = (id, creneaux) => id === 'journee' || !id ? 'Journée' : (creneaux.find(c => c.id === id)?.libelle || id);

export const CreneauSelect = ({ value, onChange, creneaux, testId, className = '' }) => {
  const cur = creneaux.find(c => c.id === value);
  return (
    <Select value={value || 'journee'} onValueChange={onChange}>
      <SelectTrigger className={`h-7 text-[11px] ${className}`} data-testid={testId} title={cur ? `${cur.heure_debut} – ${cur.heure_fin}` : 'Journée complète'}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="journee">Journée</SelectItem>
        {creneaux.map(c => <SelectItem key={c.id} value={c.id} title={`${c.heure_debut} – ${c.heure_fin}`}>{c.libelle}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

// Intervenants supplémentaires d'une journée : formateur + créneau + modules (parmi ceux de la journée).
export const IntervenantsJour = ({ date, intervenants, dayModules, allModules, formateurs, creneaux, referentId, principalId, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ formateur_id: 'none', creneau: creneaux[0]?.id || 'journee', module_ids: [] });
  const nom = (id) => { const f = formateurs.find(x => x.id === id); return f ? `${f.nom} ${f.prenom}` : '?'; };
  const code = (id) => allModules.find(m => m.id === id)?.code || '?';

  const add = () => {
    if (draft.formateur_id === 'none') return;
    onChange([...intervenants, { ...draft }]);
    setDraft({ formateur_id: 'none', creneau: creneaux[0]?.id || 'journee', module_ids: [] });
    setOpen(false);
  };
  const remove = (idx) => onChange(intervenants.filter((_, i) => i !== idx));
  const toggleModule = (mid) => setDraft(d => ({ ...d, module_ids: d.module_ids.includes(mid) ? d.module_ids.filter(x => x !== mid) : [...d.module_ids, mid] }));

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {intervenants.map((it, idx) => (
        <span key={`${it.formateur_id}-${idx}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 text-[10px]" title={it.module_ids?.length ? `Modules : ${it.module_ids.map(code).join(', ')}` : ''} data-testid={`intervenant-chip-${date}-${idx}`}>
          {nom(it.formateur_id)} · {creneauLabel(it.creneau, creneaux)}{it.module_ids?.length ? ` · ${it.module_ids.map(code).join(', ')}` : ''}
          <X className="w-2.5 h-2.5 cursor-pointer hover:text-red-600" onClick={() => remove(idx)} data-testid={`intervenant-remove-${date}-${idx}`} />
        </span>
      ))}
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && draft.formateur_id === 'none' && creneaux[0]) setDraft(d => ({ ...d, creneau: creneaux[0].id })); }}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-dashed border-slate-300 text-[10px] text-slate-500 hover:border-sky-400 hover:text-sky-700" data-testid={`intervenant-add-${date}`}>
            <UserPlus className="w-3 h-3" /> Intervenant
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[340px] p-3 space-y-2" data-testid={`intervenant-popover-${date}`}>
          <p className="text-xs font-semibold text-slate-700">Ajouter un intervenant</p>
          <Select value={draft.formateur_id} onValueChange={(v) => setDraft(d => ({ ...d, formateur_id: v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid={`intervenant-formateur-${date}`}><SelectValue placeholder="Formateur" /></SelectTrigger>
            <SelectContent><FormateurOptions formateurs={formateurs.filter(f => f.id !== principalId)} referentId={referentId} currentId={draft.formateur_id} /></SelectContent>
          </Select>
          <CreneauSelect value={draft.creneau} onChange={(v) => setDraft(d => ({ ...d, creneau: v }))} creneaux={creneaux} testId={`intervenant-creneau-${date}`} className="h-8 text-xs" />
          {dayModules.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Modules pris en charge (facultatif)</p>
              {dayModules.map(mp => (
                <label key={mp.module_id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <Checkbox checked={draft.module_ids.includes(mp.module_id)} onCheckedChange={() => toggleModule(mp.module_id)} data-testid={`intervenant-module-${date}-${mp.module_id}`} />
                  <span className="font-mono text-violet-600">{code(mp.module_id)}</span><span className="text-slate-400">{mp.duree_heures}h</span>
                </label>
              ))}
            </div>
          )}
          <Button type="button" size="sm" className="w-full h-7 text-xs bg-sky-600 hover:bg-sky-700" onClick={add} disabled={draft.formateur_id === 'none'} data-testid={`intervenant-confirm-${date}`}>Ajouter</Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
